'use client';

import { useEffect, useState } from 'react';

import Header from '@/components/common/Header';
import DashboardInteractive from './components/DashboardInteractive';
import { useAuth } from '@/contexts/AuthContext';
import { getUserEnrollmentsWithBatches } from '@/services/enrollmentService';
import { getBatchesForEnrolledCourses } from '@/services/batchService';
import { supabase } from '@/lib/supabase';

interface EnrolledCourse {
  id: string;
  title: string;
  instructor: string;
  progress: number;
  image: string;
  alt: string;
  nextClass: string;
  totalLessons: number;
  completedLessons: number;
}

interface UpcomingClass {
  id: string;
  courseTitle: string;
  batchName?: string;
  instructor: string;
  date: string;
  time: string;
  duration: string;
  isLive: boolean;
  zoomLink?: string;
}

export default function StudentDashboardPage() {
  const { user, userProfile } = useAuth();
  const [courses, setCourses] = useState<EnrolledCourse[]>([]);
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPendingPayments, setHasPendingPayments] = useState(false);

  useEffect(() => {
    function loadEnrollmentsAndClasses() {
      if (!user) {
        console.log('⚠️ [StudentDashboard] No user logged in');
        setLoading(false);
        return;
      }

      console.log('🚀 [StudentDashboard] Loading enrollments for user:', user.id);
      setLoading(true);
      setError(null);

      // Fetch user's active enrollments with batch info (filtered by payment verification)
      getUserEnrollmentsWithBatches(user.id)
        .then(({ data: enrollments, error: enrollmentError }) => {
          if (enrollmentError) {
            console.error('❌ [StudentDashboard] Enrollment error:', enrollmentError);
            setError('Failed to load your courses. Please try again.');
            setLoading(false);
            return;
          }

          // Check for pending payments by fetching all enrollments (not just verified)
          return supabase
            .from('course_enrollments')
            .select(`
              payment:payment_transactions!course_enrollments_payment_transaction_id_fkey (
                status
              )
            `)
            .eq('user_id', user.id)
            .eq('status', 'pending')
            .then(({ data: allEnrollments }) => {
              const pendingPayments = allEnrollments?.some((e: any) => e.payment?.status === 'pending') || false;
              setHasPendingPayments(pendingPayments);

              if (!enrollments || enrollments.length === 0) {
                console.log('ℹ️ [StudentDashboard] No active enrollments with verified payments');
                setCourses([]);
                setUpcomingClasses([]);
                setLoading(false);
                return;
              }

              // Fetch ALL batches for enrolled courses (already filtered by payment verification)
              return getBatchesForEnrolledCourses(user.id)
                .then(({ data: allBatchesForCourses, error: batchFetchError }) => {
                  if (batchFetchError) {
                    console.error('❌ [StudentDashboard] Batch fetch error:', batchFetchError);
                  }

                  console.log('📦 [StudentDashboard] Fetched batches:', {
                    count: allBatchesForCourses?.length || 0,
                    batches: allBatchesForCourses?.map(b => ({
                      id: b.id,
                      name: b.batch_name,
                      course: b.course_title,
                      startDate: b.start_date,
                      endDate: b.end_date,
                      schedule: b.schedule_info
                    }))
                  });

                  // Transform enrollments to course format (WITHOUT batch info)
                  const enrolledCourses: EnrolledCourse[] = enrollments.map((enrollment: any) => {
                    const course = enrollment.course || {};
                    
                    return {
                      id: course.id || enrollment.id,
                      title: course.title || 'Course',
                      instructor: course.instructor_name?.full_name || 'Instructor',
                      progress: enrollment.progress_percentage || 0,
                      image: course.thumbnail_url || '/assets/images/no_image.png',
                      alt: `${course.title || 'Course'} thumbnail`,
                      nextClass: 'See upcoming classes below',
                      totalLessons: course.duration_weeks ? course.duration_weeks * 2 : 0,
                      completedLessons: Math.floor((enrollment.progress_percentage || 0) * (course.duration_weeks ? course.duration_weeks * 2 : 0) / 100)
                    };
                  });

                  setCourses(enrolledCourses);

                  // Build upcoming classes from batches with full details
                  // Show classes that are happening today or in the future
                  const now = new Date();
                  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
                  
                  console.log('📅 [StudentDashboard] Filtering batches - Current time:', now.toISOString());
                  console.log('📅 [StudentDashboard] Today start:', todayStart.toISOString());
                  
                  const upcomingBatches = (allBatchesForCourses || [])
                    .filter((batch: any) => {
                      const batchStart = new Date(batch.start_date);
                      const batchEnd = new Date(batch.end_date);
                      
                      // Show if batch starts today or in future, AND batch is still active (not ended)
                      const isUpcoming = batchStart >= todayStart && batch.is_active && batchEnd >= now;
                      
                      console.log(`📌 [StudentDashboard] Batch ${batch.batch_name}:`, {
                        startDate: batchStart.toISOString(),
                        endDate: batchEnd.toISOString(),
                        startsToday: batchStart >= todayStart,
                        isActive: batch.is_active,
                        notEnded: batchEnd >= now,
                        willShow: isUpcoming
                      });
                      
                      return isUpcoming;
                    })
                    .sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

                  console.log('✨ [StudentDashboard] Upcoming batches after filtering:', upcomingBatches.length);

                  const classes: UpcomingClass[] = upcomingBatches.map((batch: any) => {
                    const startDate = new Date(batch.start_date);
                    const endDate = new Date(batch.end_date);
                    
                    // Use schedule_info.time for display (this is the actual class time admin entered)
                    const classTime = batch.schedule_info?.time || 'Time not set';
                    const classDays = batch.schedule_info?.days?.join(', ') || 'Days not set';
                    
                    // Check if class is currently live
                    // Since we now use schedule_info.time, we'll determine "live" based on current date
                    // and if it's one of the scheduled days
                    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
                    const isScheduledToday = batch.schedule_info?.days?.includes(currentDay) || false;
                    const isLive = isScheduledToday && startDate <= now && endDate >= now;

                    // Extract zoom link from schedule_info
                    const zoomLink = batch.schedule_info?.zoom_url || '';

                    return {
                      id: batch.id,
                      courseTitle: batch.course_title || 'Course',
                      batchName: batch.batch_name,
                      instructor: batch.instructor_name || 'Instructor',
                      date: startDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      }),
                      time: `${classDays} at ${classTime}`,
                      duration: `${Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7))} weeks`,
                      isLive: isLive,
                      zoomLink: zoomLink
                    };
                  });

                  console.log('🎓 [StudentDashboard] Final upcoming classes:', classes.length);
                  setUpcomingClasses(classes);
                  setLoading(false);
                });
            });
        })
        .catch((err: any) => {
          console.error('❌ [StudentDashboard] Unexpected error:', err);
          setError(err?.message || 'An unexpected error occurred');
          setLoading(false);
        });
    }

    loadEnrollmentsAndClasses();
  }, [user]);

  const mockAchievements = [
    {
      id: "1",
      title: "Fast Learner",
      description: "Complete 5 lessons in one day",
      icon: "BoltIcon",
      earned: true,
      earnedDate: "January 10, 2026"
    },
    {
      id: "2",
      title: "Consistent Student",
      description: "Maintain 7-day study streak",
      icon: "FireIcon",
      earned: true,
      earnedDate: "January 15, 2026"
    },
    {
      id: "3",
      title: "Course Champion",
      description: "Complete your first course",
      icon: "TrophyIcon",
      earned: false
    },
    {
      id: "4",
      title: "Community Helper",
      description: "Help 10 peers in forums",
      icon: "UserGroupIcon",
      earned: false
    },
    {
      id: "5",
      title: "Perfect Attendance",
      description: "Attend all live classes in a month",
      icon: "CheckBadgeIcon",
      earned: true,
      earnedDate: "December 31, 2025"
    },
    {
      id: "6",
      title: "Knowledge Seeker",
      description: "Enroll in 5 different courses",
      icon: "AcademicCapIcon",
      earned: false
    }
  ];

  const mockQuickAccessItems = [
    {
      id: "1",
      title: "Course Materials",
      description: "Access downloads and resources",
      icon: "FolderIcon",
      href: "/course-catalog",
      color: "bg-primary"
    },
    {
      id: "2",
      title: "Certificates",
      description: "View earned certificates",
      icon: "DocumentTextIcon",
      href: "/student/certificates",
      color: "bg-accent"
    },
    {
      id: "3",
      title: "Discussion Forum",
      description: "Connect with community",
      icon: "ChatBubbleLeftRightIcon",
      href: "/community-forum",
      color: "bg-trust-builder"
    },
    {
      id: "4",
      title: "Help Center",
      description: "Get support and answers",
      icon: "QuestionMarkCircleIcon",
      href: "/student-dashboard",
      color: "bg-brand-conversion"
    }
  ];

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50">
        <Header />
        <div className="w-full pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg p-6 lg:p-8 h-32 animate-pulse" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Header />
      
      {hasPendingPayments && courses.length === 0 && (
        <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-24">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-yellow-800">Payment Verification Pending</h3>
                <p className="mt-2 text-sm text-yellow-700">
                  Your payment is currently being verified by our admin team. Once approved, you'll be able to see your enrolled classes and batches here. This usually takes 24-48 hours.
                </p>
                <p className="mt-2 text-sm text-yellow-700 font-medium">
                  You'll receive an email notification once your payment is verified and your enrollment is activated.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <DashboardInteractive
        userName={userProfile?.full_name || 'Student'}
        currentStreak={12}
        totalPoints={2450}
        courses={courses}
        upcomingClasses={upcomingClasses}
        achievements={mockAchievements}
        learningPaths={[]}
        peerConnections={[]}
        quickAccessItems={mockQuickAccessItems}
        scheduleItems={[]}
      />
    </main>
  );
}